import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { TrendingDown, TrendingUp, Award, Scale, Plus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Progress = () => {
  const { user, weights, logWeight, totals, goals } = useApp();
  const [showLog, setShowLog] = useState(false);
  const [w, setW] = useState(String(user.currentWeight));
  const { toast } = useToast();

  const wu = user.weightUnit;
  const diff = (user.currentWeight - user.startWeight).toFixed(1);
  const toGoal = Math.abs(user.currentWeight - user.goalWeight).toFixed(1);
  const isLoss = parseFloat(diff) < 0;

  const chartData = weights.slice(-14).map((p) => p.weight);
  const labels = weights.slice(-14).map((p) => p.date.slice(8));
  const hasData = chartData.length > 1;
  const min = hasData ? Math.min(...chartData) : 0;
  const max = hasData ? Math.max(...chartData) : 1;
  const range = max - min || 1;

  const goalPct = user.startWeight !== user.goalWeight
    ? Math.min(100, Math.max(0,
        ((user.startWeight - user.currentWeight) / (user.startWeight - user.goalWeight)) * 100
      ))
    : 100;

  const handleSave = () => {
    const val = parseFloat(w);
    if (!val || val <= 0) return;
    logWeight(val);
    toast({ title: 'Weight logged', description: `Recorded ${val} ${wu}` });
    setShowLog(false);
  };

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">
      <header className="bg-white px-5 pt-5 pb-3 border-b border-gray-100 sticky top-0 z-30">
        <h1 className="text-xl font-bold text-gray-900">Progress</h1>
      </header>

      {/* Summary cards */}
      <section className="px-5 pt-4 grid grid-cols-3 gap-3">
        <StatCard label="Starting" value={`${user.startWeight}`} unit={wu} icon={<Scale size={16} />} />
        <StatCard label="Current" value={`${user.currentWeight}`} unit={wu} icon={<Award size={16} />} highlight />
        <StatCard label="Goal" value={`${user.goalWeight}`} unit={wu} icon={<TrendingDown size={16} />} />
      </section>

      {/* Change + Log button */}
      <section className="px-5 pt-4">
        <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Change</p>
            <p className={`text-2xl font-bold mt-1 flex items-center gap-1 ${isLoss ? 'text-emerald-600' : 'text-orange-500'}`}>
              {isLoss ? <TrendingDown size={22} /> : <TrendingUp size={22} />}
              {diff} {wu}
            </p>
            <p className="text-xs text-gray-500 mt-1">{toGoal} {wu} to goal</p>
          </div>
          <button
            onClick={() => { setW(String(user.currentWeight)); setShowLog(true); }}
            className="flex items-center gap-1.5 bg-[#0066ee] text-white font-semibold px-4 py-2.5 rounded-full shadow-md press-scale hover:bg-[#0055cc]"
          >
            <Plus size={16} /> Log Weight
          </button>
        </div>
      </section>

      {/* Weight Chart */}
      <section className="px-5 pt-4">
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <p className="text-sm font-bold text-gray-900 mb-3">Weight History (last 14 entries)</p>
          {hasData ? (
            <>
              <div className="relative h-48">
                <svg viewBox="0 0 300 160" className="w-full h-full" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="wGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0066ee" stopOpacity="0.2" />
                      <stop offset="100%" stopColor="#0066ee" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {[0, 1, 2, 3].map((i) => (
                    <line key={i} x1="0" x2="300" y1={20 + i * 40} y2={20 + i * 40} stroke="#f1f5f9" strokeWidth="1" />
                  ))}
                  {(() => {
                    const pts = chartData.map((v, i) => {
                      const x = (i / (chartData.length - 1)) * 290 + 5;
                      const y = 150 - ((v - min) / range) * 130;
                      return [x, y];
                    });
                    const path = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ');
                    const area = `${path} L${pts[pts.length - 1][0]},160 L${pts[0][0]},160 Z`;
                    return (
                      <>
                        <path d={area} fill="url(#wGrad)" />
                        <path d={path} fill="none" stroke="#0066ee" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        {pts.map(([x, y], i) => (
                          <circle key={i} cx={x} cy={y} r="3" fill="#fff" stroke="#0066ee" strokeWidth="2" />
                        ))}
                      </>
                    );
                  })()}
                </svg>
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                {labels.filter((_, i) => i % Math.ceil(labels.length / 7) === 0).map((l, i) => (
                  <span key={i}>{l}</span>
                ))}
              </div>
            </>
          ) : (
            <div className="h-32 flex flex-col items-center justify-center text-gray-400">
              <Scale size={32} className="mb-2 text-gray-300" />
              <p className="text-sm">No weight entries yet</p>
              <p className="text-xs mt-1">Tap "Log Weight" to start tracking</p>
            </div>
          )}
        </div>
      </section>

      {/* Goal Progress Bar */}
      <section className="px-5 pt-4">
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <p className="text-sm font-bold text-gray-900">Weight Goal Progress</p>
          <div className="mt-3 w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#0066ee] to-[#3b82f6] rounded-full transition-all duration-700"
              style={{ width: `${goalPct}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>{user.startWeight} {wu}</span>
            <span className="font-semibold text-[#0066ee]">{user.currentWeight} {wu}</span>
            <span>{user.goalWeight} {wu}</span>
          </div>
        </div>
      </section>

      {/* Today's Calories */}
      <section className="px-5 pt-4 pb-6">
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-4 border border-blue-100">
          <p className="text-xs font-bold text-[#0066ee] uppercase tracking-wider">Today's Calories</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totals.calories.toLocaleString()} cal</p>
          <p className="text-xs text-gray-500">Goal: {goals.calories.toLocaleString()} cal · Remaining: {(goals.calories - totals.calories).toLocaleString()}</p>
        </div>
      </section>

      {/* Log Weight Modal */}
      {showLog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end" onClick={() => setShowLog(false)}>
          <div
            className="w-full max-w-[480px] mx-auto bg-white rounded-t-3xl p-6 slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1.5 bg-gray-200 rounded-full mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900">Log Today's Weight</h3>
            <input
              type="number"
              step="0.1"
              value={w}
              onChange={(e) => setW(e.target.value)}
              className="mt-4 w-full text-center text-3xl font-bold py-4 bg-gray-50 rounded-2xl outline-none focus:ring-2 focus:ring-[#0066ee]"
            />
            <p className="text-center text-xs text-gray-500 mt-2">{wu}</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowLog(false)} className="flex-1 py-3 rounded-xl border border-gray-200 font-semibold text-gray-700">
                Cancel
              </button>
              <button onClick={handleSave} className="flex-1 py-3 rounded-xl bg-[#0066ee] text-white font-bold">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, unit, icon, highlight }) => (
  <div className={`rounded-2xl p-3 ${highlight ? 'bg-[#0066ee] text-white' : 'bg-white text-gray-900 shadow-sm'}`}>
    <div className={`flex items-center gap-1 ${highlight ? 'text-blue-100' : 'text-gray-500'}`}>
      {icon}
      <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
    </div>
    <p className="text-lg font-bold mt-1">{value}</p>
    <p className={`text-[10px] ${highlight ? 'text-blue-200' : 'text-gray-400'}`}>{unit}</p>
  </div>
);

export default Progress;
